import { useCallback, useEffect, useState } from 'react';
import type { ZodType } from 'zod';

/**
 * Thrown by useApi/apiMutation on any non-2xx response. Carries the HTTP
 * `status` and the server's structured error `code` (the JSON body's `error`
 * field, e.g. 'signer_email_not_configured') so callers can branch on the
 * actual cause instead of string-matching. The `message` keeps the historical
 * "<status> <statusText>: <body>" shape so existing `message.includes('409')`
 * callers keep working.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly body: string;

  constructor(status: number, statusText: string, body: string) {
    super(`${status} ${statusText}: ${body}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.code = parseErrorCode(body);
  }
}

function parseErrorCode(body: string): string | null {
  try {
    const parsed: unknown = JSON.parse(body);
    if (parsed && typeof parsed === 'object' && 'error' in parsed) {
      const code = (parsed as { error: unknown }).error;
      return typeof code === 'string' ? code : null;
    }
  } catch {
    // Non-JSON error body (plain text / empty) — no structured code to read.
  }
  return null;
}

export interface UseApiResult<T> {
  data: T | null;
  error: Error | null;
  loading: boolean;
  refetch: () => Promise<void>;
}

export function useApi<T>(url: string | null, schema?: ZodType<T>): UseApiResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);

  const refetch = useCallback(async (): Promise<void> => {
    if (!url) return;
    setLoading(true);
    try {
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        throw new ApiError(res.status, res.statusText, await res.text());
      }
      const json: unknown = await res.json();
      const parsed = schema ? schema.parse(json) : (json as T);
      setData(parsed);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
  }, [url, schema]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { data, error, loading, refetch };
}

export interface ApiMutationOpts<TOut> {
  schema?: ZodType<TOut>;
  method?: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
}

export async function apiMutation<TIn, TOut>(
  url: string,
  body: TIn,
  opts: ApiMutationOpts<TOut> = {},
): Promise<TOut> {
  const { schema, method = 'POST' } = opts;
  const res = await fetch(url, {
    method,
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new ApiError(res.status, res.statusText, await res.text());
  }
  const json: unknown = await res.json();
  return schema ? schema.parse(json) : (json as TOut);
}
